import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LoaderPinwheel } from "lucide-react";
import Navbar from "../components/ui/Navbar";

const DetalleOrdenPage = () => {
  const { id } = useParams();
  const [orden, setOrden] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemsAlmacen, setItemsAlmacen] = useState([]);
  const [modoNuevo, setModoNuevo] = useState(false);
  const [nuevoItem, setNuevoItem] = useState({
    producto: "",
    cantidad: 1,
    costo_unitario: 0,
  });

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const usuarioId = user?._id;

  const estatusStyles = {
    Creada: "bg-yellow-100 text-yellow-800",
    Pendiente: "bg-orange-100 text-orange-800",
    Autorizada: "bg-blue-100 text-blue-800",
    Pagada: "bg-green-100 text-green-800",
    Cerrada: "bg-gray-100 text-gray-800",
    Rechazada: "bg-red-100 text-red-800",
  };

  const fetchOrden = async () => {
    try {
      const res = await axios.get(`${API_URL}/ordenes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrden(res.data);
    } catch {
      toast.error("No se pudo cargar la orden de compra");
    }
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API_URL}/detalle/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data);
    } catch {
      toast.error("No se pudieron cargar los items de la orden");
    }
  };

  const fetchItemsAlmacen = async () => {
    try {
      const res = await axios.get(`${API_URL}/stock/items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItemsAlmacen(res.data);
    } catch {
      toast.error("No se pudo cargar el almacén");
    }
  };

  const fetchData = async () => {
    if (!orden) setLoading(true);
    try {
      await Promise.all([fetchOrden(), fetchItems(), fetchItemsAlmacen()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  const handleAgregarItem = async () => {
    if (orden?.estatus !== "Creada") {
      return toast.error(
        "No se pueden agregar items, la orden no está en estado Creada",
      );
    }

    if (
      !nuevoItem.producto ||
      nuevoItem.cantidad <= 0 ||
      nuevoItem.costo_unitario <= 0
    ) {
      return toast.error("Completa todos los campos del item correctamente");
    }

    try {
      const payload = {
        ...nuevoItem,
        ordenId: id,
        subtotal: nuevoItem.cantidad * nuevoItem.costo_unitario,
      };

      await axios.post(`${API_URL}/detalle`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Item agregado ✅");
      setNuevoItem({ producto: "", cantidad: 1, costo_unitario: 0 });
      fetchItems();
    } catch {
      toast.error("No se pudo agregar el item");
    }
  };

  const handleEliminarItem = async (itemId) => {
    if (orden?.estatus !== "Creada") {
      return toast.error(
        "No se pueden eliminar items, la orden no está en estado Creada",
      );
    }

    if (!window.confirm("¿Eliminar este item?")) return;

    try {
      await axios.delete(`${API_URL}/detalle/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Item eliminado ✅");
      fetchItems();
    } catch {
      toast.error("No se pudo eliminar el item");
    }
  };

  const totalItems = items.reduce((acc, i) => acc + i.subtotal, 0);
  const total = totalItems + (orden?.costologistica || 0);

  const getBotonesEstado = () => {
    switch (orden?.estatus) {
      case "Creada":
        return [{ texto: "Confirmar", next: "Autorizada" }];
      case "Pendiente":
        return [
          { texto: "Rechazar", next: "Rechazada", variant: "destructive" },
          { texto: "Autorizar", next: "Autorizada" },
        ];
      case "Autorizada":
        return [{ texto: "Pagada", next: "Pagada" }];
      case "Pagada":
        return [{ texto: "Cerrada", next: "Cerrada" }];
      default:
        return [];
    }
  };

  const handleCambiarEstado = async (nuevoEstado, forzar = false) => {
    try {
      const res = await axios.patch(
        `${API_URL}/ordenes/${id}/estatus`,
        {
          estatus: nuevoEstado,
          usuarioId,
          ...(forzar ? { forzar: true } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Orden ${nuevoEstado.toLowerCase()} ✅`);
      setOrden(res.data);
    } catch (err) {
      const data = err.response?.data;

      if (data?.requiereConfirmacion) {
        const listado = data.pendientes
          .map((p) => `- ${p.articulo}: faltan ${p.pendiente}`)
          .join("\n");
        const confirmado = window.confirm(
          `${data.message}\n\n${listado}\n\n¿Cerrar de todas formas? Esta acción no podrá revertirse.`,
        );
        if (confirmado) {
          await handleCambiarEstado(nuevoEstado, true);
        }
        return;
      }

      const msg = data?.message || "Error al cambiar el estado";
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />

      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">
        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando información de la orden...
          </div>
        )}

        <Card className="shadow-md">
          <CardContent>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                Detalle Orden: {orden?.descripcion || "—"} [{orden?._id || "—"}]
              </h2>
              <Button onClick={() => navigate(-1)}>← Volver</Button>
            </div>

            <div className="mb-6 grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded">
              <div>
                <p>
                  <strong>Proveedor:</strong> {orden?.proveedor?.name || "-"}
                </p>
                <p>
                  <strong>Cliente:</strong> {orden?.clientesId?.name || "-"}
                </p>
                <p>
                  <strong>Observaciones:</strong> {orden?.observaciones || "-"}
                </p>
              </div>

              <div>
                <p>
                  <strong>Fecha:</strong>{" "}
                  {orden?.fecha
                    ? new Date(orden.fecha).toLocaleDateString()
                    : "-"}
                </p>

                <p>
                  <strong>Estatus:</strong>{" "}
                  <span
                    className={`inline-block px-2 py-1 text-sm font-semibold rounded-full ${
                      estatusStyles[orden?.estatus] || ""
                    }`}
                  >
                    {orden?.estatus || "-"}
                  </span>
                </p>

                <p>
                  <strong>Flete:</strong>{" "}
                  {orden?.costologistica
                    ? `$ ${Number(orden.costologistica).toLocaleString()}`
                    : "—"}
                </p>

                <p>
                  <strong>Tiempo de entrega (días):</strong>{" "}
                  {orden?.tiempo_entrega || "-"}
                </p>
              </div>
            </div>

            <h3 className="font-semibold mb-2">Items de la orden</h3>

            <table className="min-w-full bg-white border rounded-lg shadow-sm mb-4">
              <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
                <tr>
                  <th className="py-2 px-4 text-left">Producto</th>
                  <th className="py-2 px-4 text-right">Cantidad</th>
                  <th className="py-2 px-4 text-right">Costo Unitario</th>
                  <th className="py-2 px-4 text-right">Subtotal</th>
                  {orden?.estatus === "Creada" && (
                    <th className="py-2 px-4 text-right">Acciones</th>
                  )}
                </tr>
              </thead>

              <tbody>
                {items.map((i, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-4">{i.producto}</td>
                    <td className="py-2 px-4 text-right">{i.cantidad}</td>
                    <td className="py-2 px-4 text-right">
                      ${Number(i.costo_unitario).toLocaleString()}
                    </td>
                    <td className="py-2 px-4 text-right">
                      ${Number(i.subtotal).toLocaleString()}
                    </td>

                    {orden?.estatus === "Creada" && (
                      <td className="py-2 px-4 text-right">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleEliminarItem(i._id)}
                        >
                          Eliminar
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {orden?.estatus === "Creada" && (
              <div className="mb-6 grid grid-cols-4 gap-4 items-end">
                <div className="col-span-1 flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Producto
                  </label>

                  {!modoNuevo ? (
                    <>
                      <select
                        value={nuevoItem.producto}
                        onChange={(e) =>
                          setNuevoItem({
                            ...nuevoItem,
                            producto: e.target.value,
                          })
                        }
                        className="h-10 border rounded-md px-2 w-full"
                      >
                        <option value="">Seleccionar ítem</option>
                        {itemsAlmacen.map((item) => (
                          <option key={item.codigo_item} value={item.articulo}>
                            {item.articulo}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => setModoNuevo(true)}
                      >
                        + Crear nuevo ítem
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        placeholder="Nombre del nuevo ítem"
                        value={nuevoItem.producto}
                        onChange={(e) =>
                          setNuevoItem({
                            ...nuevoItem,
                            producto: e.target.value,
                          })
                        }
                        className="h-10 border rounded-md px-2"
                      />

                      <button
                        type="button"
                        className="text-xs text-gray-500 hover:underline"
                        onClick={() => setModoNuevo(false)}
                      >
                        ← Seleccionar del almacén
                      </button>
                    </>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium">Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    value={nuevoItem.cantidad}
                    onChange={(e) =>
                      setNuevoItem({
                        ...nuevoItem,
                        cantidad: Number(e.target.value),
                      })
                    }
                    className="h-10 border rounded-md px-2 w-full"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Costo unitario</label>
                  <input
                    type="number"
                    value={nuevoItem.costo_unitario}
                    onChange={(e) =>
                      setNuevoItem({
                        ...nuevoItem,
                        costo_unitario: Number(e.target.value),
                      })
                    }
                    className="h-10 border rounded-md px-2 w-full"
                  />
                </div>

                <Button onClick={handleAgregarItem}>+ Agregar ítem</Button>
              </div>
            )}

            <div className="text-right font-semibold mb-4">
              Total: ${total.toLocaleString()}
            </div>

            <div className="flex justify-end gap-2">
              {getBotonesEstado().map((btn, idx) => (
                <Button
                  key={idx}
                  onClick={() => handleCambiarEstado(btn.next)}
                  className={
                    btn.variant === "destructive"
                      ? "bg-red-600 text-white"
                      : "bg-blue-600 text-white"
                  }
                >
                  {btn.texto}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DetalleOrdenPage;
